import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Search, Car, Info, Loader2, AlertCircle, CheckCircle,
  Calendar, Fuel, Settings, MapPin, Cog, Lock
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * VIN Decoder Seite für User
 * Dekodiert VINs und zeigt Fahrzeugdaten an
 */
const VINDecoderPage = () => {
  const { subscription, user } = useAuth();
  const [vin, setVin] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);

  // Check if user has VIN decoder access - Admin always has access
  const isAdmin = user?.is_admin === true;
  const hasAccess = isAdmin || subscription?.features?.vin_decoder === true;

  const decodeVIN = async () => {
    if (vin.length !== 17) {
      setError('VIN muss genau 17 Zeichen haben');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const token = localStorage.getItem('token');
      // Use the user endpoint, not the admin-only endpoint
      const response = await axios.post(
        `${API}/api/admin/vehicle-master/vin/decode-user`,
        { vin: vin.toUpperCase() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setResult(response.data.data);
        // Add to history
        setHistory(prev => {
          const newHistory = [response.data.data, ...prev.filter(h => h.vin !== vin)].slice(0, 5);
          return newHistory;
        });
      } else {
        setError(response.data.error || 'VIN konnte nicht dekodiert werden');
      }
    } catch (err) {
      console.error('VIN decode error:', err);
      setError(err.response?.data?.detail || 'Fehler beim Dekodieren der VIN');
    } finally {
      setLoading(false);
    }
  };

  // No access view
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#1a2332] to-[#0d1929] p-6">
        <div className="max-w-2xl mx-auto">
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-12 text-center">
              <Lock className="w-16 h-16 mx-auto text-gray-500 mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">VIN Decoder</h2>
              <p className="text-gray-400 mb-6">
                Der VIN Decoder ist in Ihrem aktuellen Plan nicht enthalten.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Upgrade auf einen höheren Plan, um Zugriff auf den VIN Decoder zu erhalten.
              </p>
              <Button 
                className="bg-gradient-to-r from-cyan-500 to-blue-600"
                onClick={() => window.location.href = '/pricing'}
              >
                Pläne ansehen
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#1a2332] to-[#0d1929] p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">VIN Decoder</h1>
          <p className="text-gray-400">
            Geben Sie eine 17-stellige Fahrgestellnummer ein, um Fahrzeugdaten zu erhalten
          </p>
        </div>

        {/* Search Card */}
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-6">
            <div className="flex gap-4">
              <div className="flex-1">
                <Input
                  value={vin}
                  onChange={(e) => setVin(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                  placeholder="z.B. WVWZZZ3CZWE123456"
                  maxLength={17}
                  className="bg-white/10 border-white/20 text-white text-lg font-mono tracking-wider h-12"
                />
                <div className="flex justify-between mt-2">
                  <span className="text-xs text-gray-500">
                    {vin.length}/17 Zeichen
                  </span>
                  {vin.length === 17 && (
                    <span className="text-xs text-green-400 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Gültige Länge
                    </span>
                  )}
                </div>
              </div>
              <Button
                onClick={decodeVIN}
                disabled={loading || vin.length !== 17}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 h-12 px-8"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Search className="w-5 h-5 mr-2" />
                    Dekodieren
                  </>
                )}
              </Button>
            </div>

            {error && (
              <div className="mt-4 p-4 bg-red-900/20 border border-red-500/30 rounded-lg flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-400" />
                <span className="text-red-400">{error}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Result Card */}
        {result && (
          <Card className="bg-white/5 border-white/10">
            <CardHeader className="border-b border-white/10">
              <CardTitle className="text-white flex items-center gap-3">
                <Car className="w-6 h-6 text-cyan-400" />
                {result.brand} {result.model}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {/* Main Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <InfoCard 
                  icon={Car} 
                  label="Hersteller" 
                  value={result.manufacturer || result.brand} 
                  color="cyan"
                />
                <InfoCard 
                  icon={Calendar} 
                  label="Baujahr" 
                  value={result.model_year || result.production_year} 
                  color="blue"
                />
                <InfoCard 
                  icon={Fuel} 
                  label="Kraftstoff" 
                  value={result.fuel_type} 
                  color="green"
                />
                <InfoCard 
                  icon={Settings} 
                  label="Getriebe" 
                  value={result.transmission_type} 
                  color="purple"
                />
              </div>

              {/* Detailed Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Left Column */}
                <div className="space-y-3">
                  <DetailRow label="VIN" value={result.vin} mono />
                  <DetailRow label="Karosserie" value={result.body_type} />
                  <DetailRow label="Motorcode" value={result.engine_code} mono />
                  <DetailRow label="Hubraum" value={result.engine_displacement_cc ? `${result.engine_displacement_cc} ccm` : null} />
                  <DetailRow label="Antrieb" value={result.drive_type} />
                </div>
                
                {/* Right Column */}
                <div className="space-y-3">
                  <DetailRow label="Werk" value={result.plant_location} />
                  <DetailRow label="Markt" value={result.market} />
                  <DetailRow label="Lenkung" value={result.steering} />
                  <DetailRow label="Farbcode" value={result.color_code} mono />
                  <DetailRow label="Ausstattung" value={result.equipment_level} />
                </div>
              </div>

              {/* PR Codes (VW/Audi) */}
              {result.pr_codes && result.pr_codes.length > 0 && (
                <div className="mt-6 p-4 bg-white/5 rounded-lg">
                  <h4 className="text-cyan-400 font-medium mb-3 flex items-center gap-2">
                    <Cog className="w-4 h-4" />
                    Ausstattungscodes (PR-Codes)
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {result.pr_codes.map((code, i) => (
                      <span 
                        key={i}
                        className="px-2 py-1 bg-white/10 text-white text-xs font-mono rounded"
                      >
                        {code}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Confidence */}
              {result.confidence && (
                <div className="mt-4 flex items-center gap-2 text-xs text-gray-500">
                  <Info className="w-3 h-3" />
                  Zuverlässigkeit: {Math.round(result.confidence * 100)}%
                  {result.data_source && ` | Quelle: ${result.data_source}`}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* History */}
        {history.length > 0 && !result && (
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white text-lg">Letzte Abfragen</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-2">
                {history.map((item, i) => (
                  <div 
                    key={i}
                    className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 cursor-pointer transition-colors"
                    onClick={() => {
                      setVin(item.vin);
                      setResult(item);
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <Car className="w-4 h-4 text-cyan-400" />
                      <span className="text-white">{item.brand} {item.model}</span>
                    </div>
                    <span className="font-mono text-xs text-gray-400">{item.vin}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Help Text */}
        <div className="text-center text-sm text-gray-500">
          <p>Die VIN finden Sie im Fahrzeugschein (Feld E) oder an der Windschutzscheibe unten links.</p>
        </div>
      </div>
    </div>
  );
};

// Helper Components
const InfoCard = ({ icon: Icon, label, value, color = 'cyan' }) => {
  const colors = {
    cyan: 'text-cyan-400 bg-cyan-500/10',
    blue: 'text-blue-400 bg-blue-500/10',
    green: 'text-green-400 bg-green-500/10',
    purple: 'text-purple-400 bg-purple-500/10',
  };

  return (
    <div className={`p-4 rounded-lg ${colors[color].split(' ')[1]}`}>
      <Icon className={`w-5 h-5 ${colors[color].split(' ')[0]} mb-2`} />
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-white font-medium">{value || '-'}</p>
    </div>
  );
};

const DetailRow = ({ label, value, mono = false }) => {
  if (!value) return null;
  return (
    <div className="flex justify-between items-center p-2 bg-white/5 rounded">
      <span className="text-gray-400 text-sm">{label}</span>
      <span className={`text-white text-sm ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
};

export default VINDecoderPage;
